import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Car, AlertTriangle, Phone, Gauge, Activity, Search,
  Clock, User as UserIcon, X, Zap, Shield, Navigation, Users
} from 'lucide-react';

const API = 'https://api.lifeshare.app';

interface Stats {
  period_days: number;
  trips: { total: number; total_distance_km: number; avg_driving_score: number | null; active_drivers: number };
  events: { total: number; hard_brakes: number; rapid_accels: number; speeding: number; phone_usage: number };
}

interface Driver {
  user_id: number; username: string; full_name: string; avatar_url: string | null;
  trip_count: number; total_distance_km: number; avg_score: number | null;
  hard_brakes: number; rapid_accels: number; speeding: number;
  phone_usage_seconds: number; risk_events: number; max_speed_kmh: number;
}

interface DrivingEvent {
  id: number; user_id: number; user_name: string; avatar_url: string | null;
  trip_id: number; event_type: string; severity: string | null;
  speed_kmh: number | null; g_force: number | null;
  latitude: number | null; longitude: number | null; created_at: string;
}

const StatCard: React.FC<{ icon: React.ReactNode; value: number | string; label: string; color: string }> = ({ icon, value, label, color }) => {
  const colors: Record<string, string> = {
    green: 'from-green-50 to-green-100 border-green-200 text-green-700',
    blue: 'from-blue-50 to-blue-100 border-blue-200 text-blue-700',
    purple: 'from-purple-50 to-purple-100 border-purple-200 text-purple-700',
    gray: 'from-gray-50 to-gray-100 border-gray-200 text-gray-600',
    red: 'from-red-50 to-red-100 border-red-200 text-red-600',
    orange: 'from-orange-50 to-orange-100 border-orange-200 text-orange-700',
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-2.5 text-center`}>
      <div className="flex justify-center mb-1 opacity-60 [&>svg]:w-4 [&>svg]:h-4">{icon}</div>
      <p className="text-xl font-semibold">{value}</p>
      <p className="text-[10px] opacity-60 uppercase tracking-wide">{label}</p>
    </div>
  );
};

const eventLabels: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  hard_brake: { label: 'Sert Fren', color: 'bg-red-100 text-red-700', icon: <AlertTriangle className="w-3 h-3" /> },
  rapid_acceleration: { label: 'Ani Hızlanma', color: 'bg-orange-100 text-orange-700', icon: <Zap className="w-3 h-3" /> },
  speeding: { label: 'Hız Aşımı', color: 'bg-purple-100 text-purple-700', icon: <Gauge className="w-3 h-3" /> },
  phone_usage: { label: 'Telefon Kullanımı', color: 'bg-blue-100 text-blue-700', icon: <Phone className="w-3 h-3" /> },
};

const getScoreColor = (score: number | null) => {
  if (!score) return 'text-gray-400';
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-orange-500';
  return 'text-red-600';
};

const getScoreBg = (score: number | null) => {
  if (!score) return 'bg-gray-100';
  if (score >= 80) return 'bg-green-50 border-green-200';
  if (score >= 60) return 'bg-orange-50 border-orange-200';
  return 'bg-red-50 border-red-200';
};

const DrivingManagement: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [events, setEvents] = useState<DrivingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'drivers' | 'events'>('drivers');
  const [days, setDays] = useState(7);
  const [eventFilter, setEventFilter] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState('trips');
  const [selected, setSelected] = useState<Driver | null>(null);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/api/v1/admin/driving/stats?days=${days}`);
      setStats(data);
    } catch (e) { console.error(e); }
  };

  const fetchDrivers = async () => {
    try {
      const { data } = await axios.get(`${API}/api/v1/admin/driving/users?days=${days}&sort_by=${sortBy}&limit=100`);
      setDrivers(data.users);
    } catch (e) { console.error(e); }
  };

  const fetchEvents = async () => {
    try {
      const params: any = { days, limit: 100 };
      if (eventFilter) params.event_type = eventFilter;
      const { data } = await axios.get(`${API}/api/v1/admin/driving/events`, { params });
      setEvents(data.events);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchStats(); fetchDrivers(); fetchEvents(); }, [days]);
  useEffect(() => { fetchDrivers(); }, [sortBy]);
  useEffect(() => { fetchEvents(); }, [eventFilter]);
  useEffect(() => { if (stats) setLoading(false); }, [stats]);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Sürüş Güvenliği</h1>
            <p className="text-sm text-gray-500 mt-1">Sürüş puanları, olaylar ve risk analizi</p>
          </div>
          <div className="flex gap-2">
            {[7, 14, 30].map(d => (
              <button key={d} onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${days === d ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
                {d} Gün
              </button>
            ))}
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
            <StatCard icon={<Car />} value={stats.trips.total} label="Yolculuk" color="purple" />
            <StatCard icon={<Navigation />} value={`${stats.trips.total_distance_km} km`} label="Mesafe" color="blue" />
            <StatCard icon={<Shield />} value={stats.trips.avg_driving_score ?? '—'} label="Ort. Puan" color="green" />
            <StatCard icon={<Users />} value={stats.trips.active_drivers} label="Sürücü" color="gray" />
            <StatCard icon={<AlertTriangle />} value={stats.events.hard_brakes} label="Sert Fren" color="red" />
            <StatCard icon={<Zap />} value={stats.events.rapid_accels} label="Ani Hızlanma" color="orange" />
            <StatCard icon={<Gauge />} value={stats.events.speeding} label="Hız Aşımı" color="purple" />
            <StatCard icon={<Phone />} value={stats.events.phone_usage} label="Telefon" color="blue" />
          </div>
        )}

        {/* Tab Switch */}
        <div className="bg-white rounded-2xl p-4 mb-6">
          <div className="flex gap-2 flex-wrap items-center">
            <button onClick={() => setTab('drivers')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === 'drivers' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              Sürücüler ({drivers.length})
            </button>
            <button onClick={() => setTab('events')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === 'events' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              Olaylar ({events.length})
            </button>

            {tab === 'drivers' && (
              <div className="ml-auto flex gap-2">
                {[
                  { key: 'trips', label: 'En Çok Yolculuk' },
                  { key: 'score', label: 'En Düşük Puan' },
                  { key: 'events', label: 'En Çok Risk' },
                ].map(s => (
                  <button key={s.key} onClick={() => setSortBy(s.key)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${sortBy === s.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            )}

            {tab === 'events' && (
              <div className="ml-auto flex gap-2">
                <button onClick={() => setEventFilter(null)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${!eventFilter ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  Tümü
                </button>
                {Object.entries(eventLabels).map(([key, v]) => (
                  <button key={key} onClick={() => setEventFilter(key)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${eventFilter === key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    {v.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Drivers Grid */}
        {tab === 'drivers' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {drivers.map(d => (
              <div key={d.user_id}
                className="bg-white rounded-2xl p-4 hover:ring-2 hover:ring-purple-200 transition-all cursor-pointer"
                onClick={() => setSelected(d)}>
                {/* Header */}
                <div className="flex items-start gap-3 mb-3">
                  {d.avatar_url ? (
                    <img src={d.avatar_url.startsWith('/') ? `${API}${d.avatar_url}` : d.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-600 font-semibold text-lg">{(d.full_name || d.username || '?').charAt(0).toUpperCase()}</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{d.full_name || d.username}</p>
                    <p className="text-xs text-gray-500">@{d.username}</p>
                  </div>
                  {/* Score Badge */}
                  <div className={`px-2.5 py-1 rounded-xl border text-sm font-bold ${getScoreBg(d.avg_score)} ${getScoreColor(d.avg_score)}`}>
                    {d.avg_score ?? '—'}
                  </div>
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="text-center p-2 bg-gray-50 rounded-xl">
                    <p className="text-sm font-semibold text-gray-900">{d.trip_count}</p>
                    <p className="text-[10px] text-gray-500">Yolculuk</p>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded-xl">
                    <p className="text-sm font-semibold text-gray-900">{d.total_distance_km} km</p>
                    <p className="text-[10px] text-gray-500">Mesafe</p>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded-xl">
                    <p className="text-sm font-semibold text-gray-900">{d.max_speed_kmh}</p>
                    <p className="text-[10px] text-gray-500">Max km/h</p>
                  </div>
                </div>

                {/* Risk Badges */}
                <div className="flex flex-wrap gap-1.5">
                  {d.hard_brakes > 0 && <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700">Fren: {d.hard_brakes}</span>}
                  {d.rapid_accels > 0 && <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-700">Hızlanma: {d.rapid_accels}</span>}
                  {d.speeding > 0 && <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700">Hız Aşımı: {d.speeding}</span>}
                  {d.phone_usage_seconds > 0 && <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">Telefon: {Math.round(d.phone_usage_seconds / 60)}dk</span>}
                  {d.risk_events === 0 && <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700">Risksiz Sürücü</span>}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-3 mt-3 border-t border-gray-100 text-xs text-gray-400">
                  <div className="flex items-center gap-1">
                    <UserIcon className="w-3 h-3" />
                    <span>#{d.user_id}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    <span>{d.risk_events} risk olayı</span>
                  </div>
                </div>
              </div>
            ))}

            {drivers.length === 0 && (
              <div className="col-span-full bg-white rounded-2xl p-12 text-center">
                <Car className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Bu dönemde sürüş verisi bulunamadı</p>
              </div>
            )}
          </div>
        )}

        {/* Events List */}
        {tab === 'events' && (
          <div className="space-y-3">
            {events.map(e => {
              const ev = eventLabels[e.event_type] || { label: e.event_type, color: 'bg-gray-100 text-gray-700', icon: <Activity className="w-3 h-3" /> };
              return (
                <div key={e.id} className="bg-white rounded-2xl p-4 flex items-center gap-4">
                  {/* Avatar */}
                  {e.avatar_url ? (
                    <img src={e.avatar_url.startsWith('/') ? `${API}${e.avatar_url}` : e.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-600 font-semibold">{(e.user_name || '?').charAt(0).toUpperCase()}</span>
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-gray-900 text-sm truncate">{e.user_name}</p>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${ev.color}`}>
                        {ev.icon} {ev.label}
                      </span>
                    </div>
                    <div className="flex gap-3 text-xs text-gray-500">
                      {e.speed_kmh && <span>Hız: {e.speed_kmh} km/h</span>}
                      {e.g_force && <span>G: {e.g_force}</span>}
                      <span>Trip #{e.trip_id}</span>
                    </div>
                  </div>

                  {/* Time */}
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-gray-500">{e.created_at ? new Date(e.created_at).toLocaleDateString('tr-TR') : '—'}</p>
                    <p className="text-[10px] text-gray-400">{e.created_at ? new Date(e.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : ''}</p>
                  </div>
                </div>
              );
            })}

            {events.length === 0 && (
              <div className="bg-white rounded-2xl p-12 text-center">
                <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Bu dönemde sürüş olayı bulunamadı</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Driver Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50" onClick={() => setSelected(null)}>
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl max-h-[90vh] overflow-auto rounded-t-3xl" onClick={e => e.stopPropagation()}>
            <div className="sm:hidden flex justify-center pt-2 pb-1">
              <div className="w-9 h-1 bg-gray-300 rounded-full" />
            </div>

            {/* Header */}
            <div className="px-5 py-4 flex items-center justify-between border-b border-gray-100">
              <div className="flex items-center gap-3">
                {selected.avatar_url ? (
                  <img src={selected.avatar_url.startsWith('/') ? `${API}${selected.avatar_url}` : selected.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <span className="text-purple-600 font-semibold text-xl">{(selected.full_name || selected.username || '?').charAt(0).toUpperCase()}</span>
                  </div>
                )}
                <div>
                  <p className="font-semibold text-gray-900">{selected.full_name || selected.username}</p>
                  <p className="text-sm text-gray-500">@{selected.username}</p>
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            {/* Score */}
            <div className="px-5 py-4 text-center">
              <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full border-4 ${getScoreBg(selected.avg_score)}`}>
                <span className={`text-3xl font-bold ${getScoreColor(selected.avg_score)}`}>{selected.avg_score ?? '—'}</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">Sürüş Puanı</p>
            </div>

            {/* Stats Grid */}
            <div className="px-5 pb-4 grid grid-cols-2 gap-3">
              <div className="p-3 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Yolculuk</p>
                <p className="text-lg font-semibold text-gray-900">{selected.trip_count}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Toplam Mesafe</p>
                <p className="text-lg font-semibold text-gray-900">{selected.total_distance_km} km</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Max Hız</p>
                <p className="text-lg font-semibold text-gray-900">{selected.max_speed_kmh} km/h</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Risk Olayları</p>
                <p className={`text-lg font-semibold ${selected.risk_events > 0 ? 'text-red-600' : 'text-green-600'}`}>{selected.risk_events}</p>
              </div>
            </div>

            {/* Event Details */}
            <div className="px-5 pb-5 space-y-2">
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Olay Detayları</p>
              <div className="p-3 bg-red-50 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-600" /><span className="text-sm text-red-700">Sert Fren</span></div>
                <span className="text-sm font-bold text-red-700">{selected.hard_brakes}</span>
              </div>
              <div className="p-3 bg-orange-50 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-orange-600" /><span className="text-sm text-orange-700">Ani Hızlanma</span></div>
                <span className="text-sm font-bold text-orange-700">{selected.rapid_accels}</span>
              </div>
              <div className="p-3 bg-purple-50 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-2"><Gauge className="w-4 h-4 text-purple-600" /><span className="text-sm text-purple-700">Hız Aşımı</span></div>
                <span className="text-sm font-bold text-purple-700">{selected.speeding}</span>
              </div>
              <div className="p-3 bg-blue-50 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-blue-600" /><span className="text-sm text-blue-700">Telefon Kullanımı</span></div>
                <span className="text-sm font-bold text-blue-700">{selected.phone_usage_seconds > 0 ? `${Math.round(selected.phone_usage_seconds / 60)} dk` : '0'}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrivingManagement;
